import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";

// Images (wired to exact filenames — upload to src/assets/ to resolve)
import heroImage from "@/assets/mp-concrete-footing-installation-paramus-bergen-co-nj-7526w.webp";
import warningSignImage from "@/assets/foundation-crack-warning-sign-concrete-footing-totowa.webp";
import anchorBoltsImage from "@/assets/finished-concrete-footing-anchor-bolts-totowa.webp";
import screedBoardImage from "@/assets/concrete-footing-installation-screed-board-totowa.webp";
import trowelDetailImage from "@/assets/concrete-footing-finishing-trowel-detail-totowa.webp";

const SourceLink = ({ num, children }: { num: number; children: React.ReactNode }) => (
  <>
    <a href={`#source-${num}`} className="text-secondary hover:underline">
      {children}
    </a>
    <a href={`#source-${num}`} className="text-secondary hover:underline">
      <sup>{num}</sup>
    </a>
  </>
);

const faqs = [
  {
    question: "How long does it take to schedule concrete footing work in North Jersey?",
    answer: "We can usually get out to your property within a few days of your call. After a look at the site, we'll tell you whether you need a repair or a full replacement and give you a start date. Weather plays a part too, since heavy rain or a hard freeze can push a pour back a day or two, but we keep you posted so you're never left wondering when the crew shows up.",
  },
  {
    question: "What happens when your crew comes to look at my footings?",
    answer: "We start by checking your foundation walls, floors, and any doors or windows that stick, then we dig a small test hole near the problem to see what the soil looks like below the frost line. That usually tells us whether you're dealing with settling, erosion, or a footing that was never sized right for the ground. Most of the time we can give you a straight answer on that first visit.",
  },
  {
    question: "Why do so many North Jersey homes have footing cracks?",
    answer: "Clay-heavy soil is the biggest reason, since it holds water and then shrinks as it dries, pushing and pulling on footings all year. Add freeze-thaw cycles every winter and small cracks turn into bigger ones over a few seasons. It happens slowly, so you might not catch it until a door starts sticking or a crack shows up on the basement wall.",
  },
  {
    question: "How deep do concrete footings need to be in North Jersey?",
    answer: "Footings here need to sit below the 36-inch frost line, which is the minimum across northern New Jersey. Any shallower and you risk frost heave, where the freezing ground pushes the footing out of place. This holds for additions, garages, and deck piers, and we'll dig deeper when the soil underneath is soft or full of old roots.",
  },
  {
    question: "Do I need a permit for concrete footings in Totowa?",
    answer: "Yes, any footing tied to a structure needs a permit under New Jersey and Passaic County building codes. That covers additions, decks, taller retaining walls, and new foundations, and the town will want a site plan plus an inspection of the excavation and rebar before the pour. We handle the paperwork and the scheduling for you.",
  },
  {
    question: "How do I know if my footings need repair or full replacement?",
    answer: "Watch for horizontal or stair-step cracks in the foundation walls, uneven floors, sticking doors, or gaps where a porch meets the house. Any of those can mean a cracked, settled, or eroded footing, and if you're seeing two or more together it's worth having someone check. Most footing trouble builds up season after season, so catching it early usually means a simpler fix.",
  },
];

const sources = [
  {
    num: 1,
    text: "New Jersey Department of Community Affairs, Construction Code Communicator — New Jersey frost-line zones (northern New Jersey minimum 36 inches).",
    url: "https://www.nj.gov/dca/codes/publications/pdf_ccc/CCC_Fall_2023.pdf",
  },
  {
    num: 2,
    text: "Air entrainment for freeze-thaw durability (ACI and Portland Cement Association) — Federal Highway Administration, \"Air Entrainment and Concrete Durability.\"",
    url: "https://www.fhwa.dot.gov/pavement/pubs/hif17009.pdf",
  },
  {
    num: 3,
    text: "American Concrete Institute, ACI 301 — concrete reaches roughly 70% of design strength by 7 days and full design strength by 28 days, via FHWA \"Guide for Curing of Portland Cement Concrete Pavements.\"",
    url: "https://www.fhwa.dot.gov/publications/research/infrastructure/pavements/pccp/05038/007.cfm",
  },
];

export default function Footings() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Concrete Footings Totowa, NJ | Masonry Pro Construction";
  // Long-form service copy retained for structured-data use. Not used for meta tags.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const description = "Need concrete footings in Totowa or North Jersey? We prep the base, dig below frost, and pour footings built for North Jersey clay and freeze-thaw. Call for a free estimate.";
  const metaDescription = "Need concrete footings in Totowa or North Jersey? We dig below the frost line and pour footings built for local clay soil and freeze-thaw conditions here.";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title} />
        <meta name="twitter:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Concrete Footings",
            description: "Concrete footings poured below the frost line in Totowa and across North Jersey, for additions, garages, and decks.",
            url: "/concrete-services/footings",
            image: "https://masonry-pro.com/og/concrete-footings.jpg"
          }))}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero */}
          <section className="relative overflow-hidden" style={{ backgroundColor: "#0d1017", paddingTop: "160px", paddingBottom: "80px" }}>
            <img
              src={heroImage}
              alt="Concrete footing installation with rebar in a residential backyard in Paramus, Bergen County, NJ by Masonry Pro Construction"
              width={1200}
              height={800}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))" }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: "#f5f5f5", fontSize: "3rem", fontWeight: "bold", lineHeight: 1.1, marginBottom: "1rem", textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>
                  Local Concrete Footings in <span style={{ color: "#d4a853", WebkitTextFillColor: "#d4a853" }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "1.25rem", maxWidth: "48rem", margin: "0 auto 1.5rem", textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
                  The below-ground base that keeps steps, walls, and additions from shifting through our freeze-thaw winters
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Intro */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-muted-foreground">
                  Footings are the part of a building that no one sees, and almost nobody thinks about, until something starts to move that shouldn't. They sit below the ground and carry the weight of whatever goes on top, whether that's a room addition, a garage, a deck, or a set of front steps. We're a family-owned masonry company based in Totowa, NJ. We've been pouring footings across North Jersey for over 35 years, and we know what holds up in our weather and what doesn't. Get the footing right and everything above it stays put, and get it wrong, and you could be chasing cracks for years. Footings are one part of the work we do as a <Link to="/concrete-services" className="text-secondary hover:underline">concrete contractor in Totowa</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Section 1 — Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Warning Signs You Need <span className="text-gradient-gold">New Concrete Footings</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>Most people don't think about their footings until something goes wrong somewhere they can see. A door starts to stick in its frame, or a crack shows up on the basement wall and slowly gets longer. By the time you notice, the footing problem underneath has usually been building for a while.</p>
                    <p>We get calls like this around Totowa, Wayne, Clifton, and the rest of North Jersey pretty much every week, and it's often a homeowner who spotted a crack in their foundation block or noticed the front stoop pulling away from the house. They figure it's a wall issue or a step issue, when the real trouble is sitting below the surface.</p>
                    <p>Here's what to keep an eye out for:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Cracks in foundation walls, especially horizontal or stair-step patterns in block or brick</li>
                      <li>Floors that feel uneven or slope toward one side of the house</li>
                      <li>Doors and windows that stick, jam, or swing open on their own</li>
                      <li>Gaps where a porch, stoop, or addition meets the main structure</li>
                      <li>Standing water near the base of your foundation after it rains</li>
                    </ul>
                    <p>Any one of those can point to a footing that has cracked, settled, or worn away underneath, and a lot of it comes back to the soil. Much of North Jersey sits on clay-heavy ground that traps moisture and then shrinks as it dries out, and that steady push and pull puts stress on footings, especially ones that weren't sized or reinforced for that kind of movement.</p>
                    <p>Freeze-thaw is the other big one, and it works on you slowly. Water finds its way into a small crack over the fall, then freezes and expands once the cold sets in, so by the time everything thaws out in spring that hairline you could barely see has opened into a real crack. That pressure builds a little more every winter until the concrete gives.</p>
                    <p>Many people overlook an important point: a footing doesn't fail all at once; it gradually shifts over time. This slow movement can lead to subtle damage that's easy to overlook. You may become accustomed to a sticking back door, and that hairline crack might seem like it's always been there. However, just because you stop noticing these signs doesn't mean the underlying movement has stopped. If you notice two or more of these signs together, it's wise to have a professional take a look before the problem escalates into a bigger, more costly issue.</p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden lg:sticky lg:top-24">
                  <img
                    src={warningSignImage}
                    alt="Flashlight on a cracked cinder block foundation wall in a Totowa basement, a warning sign of footing trouble"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 — Image LEFT, Content RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="aspect-[4/3] rounded-xl overflow-hidden lg:sticky lg:top-24">
                  <img
                    src={anchorBoltsImage}
                    alt="Finished concrete footing and foundation wall with anchor bolts on a North Jersey job site"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Footing Types for <span className="text-gradient-gold">Additions, Garages, and Decks</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>Not every construction project requires the same type of footing. Many issues arise when someone assumes that one type of footing is suitable for all projects. For example, a room addition exerts different loads on the ground compared to a detached garage or a raised deck. The footing must match the specific job.</p>
                    <p>Here's how we usually break it down:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Continuous strip footings</strong> for home additions. These run along the full length of the new foundation walls and spread the weight of framing, a roof, and sometimes a second story across a wide base. On the clay-heavy lots we see around here, we'll often size these wider than the code minimum so they have more to sit on.</li>
                      <li><strong>Spread footings</strong>, also called pad footings, for detached garages and post-supported structures. Each one is a single square or rectangle of concrete placed under a load point, kind of like a landing pad for a column or post.</li>
                      <li><strong>Pier footings</strong> for decks. These are round holes drilled and filled with concrete, and even though a deck doesn't carry the weight of a house, frost can still shove a shallow pier right out of the ground, so we drill every pier below the frost line.</li>
                    </ul>
                    <p>The right footing depends on the soil underneath, which is why the ground matters as much as the footing itself. Clay retains moisture and expands, while sandy soil drains quickly but can shift under heavy loads. In Totowa, we've hit both on the same property. The first few feet of soil usually tell us what to expect, and if we find soft fill or old tree roots while digging, we go deeper or widen the base rather than pour on unstable ground.</p>
                    <p>It also matters how a new addition footing connects with the existing foundation. If those two don't work together, one part of the house may settle at a different rate than another, and cracks show up fast. To prevent that, we use doweled rebar connections to tie the new concrete to the old, so the whole structure moves as one unit instead of pulling apart.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 — Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Permits and Site Prep <span className="text-gradient-gold">for Totowa Projects</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>Permits are often an afterthought for many homeowners, so we address them early. Totowa is subject to New Jersey and Passaic County building codes, which require a permit from the local building department for any footing tied to a structure. This includes additions, decks, retaining walls above a certain height, and new foundations. We take care of the paperwork, but it helps for homeowners to understand what's involved.</p>
                    <p>The town will require a site plan showing the location and depth of the footings, along with the load they will support. An inspector visits before we pour to check the excavation and the rebar. This is a standard step when you are prepared for it.</p>
                    <p>Good site prep matters because concrete is only as good as what sits beneath it. Here's how we prepare the ground:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Mark out the footing locations with string lines and batter boards so everything is square and level.</li>
                      <li>Excavate below the frost line, which for North Jersey sits at <SourceLink num={1}>36 inches</SourceLink>.</li>
                      <li>Check the soil at the bottom of the trench, and if it's soft or wet, dig it out and bring in gravel that will compact.</li>
                      <li>Lay a gravel base, usually 4 to 6 inches, and pack it down with a plate tamper.</li>
                      <li>Set and tie the rebar, then call for inspection before any concrete goes in.</li>
                    </ul>
                    <p>Skipping any of these steps leads to costly issues later. A soft spot under a footing causes settling, the footing drops a quarter inch on one side, and your wall or stoop starts leaning. We've removed other crews' work around Totowa and Woodland Park where footings were poured straight onto clay with no gravel base, and that is the kind of shortcut that costs you twice.</p>
                    <p>Drainage is just as important as depth, so we grade the site to move water away from the concrete. Water pooling around a footing during the spring thaw is exactly what freeze-thaw needs to break it apart. That matters even more on the low-lying lots near the Passaic River, where the water table sits higher.</p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden lg:sticky lg:top-24">
                  <img
                    src={screedBoardImage}
                    alt="Concrete footing being screeded level in a formed trench during a Totowa home addition"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 — Image LEFT, Content RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="aspect-[4/3] rounded-xl overflow-hidden lg:sticky lg:top-24">
                  <img
                    src={trowelDetailImage}
                    alt="Mason hand-finishing a concrete footing surface with a float in Totowa, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Our Concrete Footing <span className="text-gradient-gold">Installation Process</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>When it comes to pouring footings, we follow a solid, dependable process every time. No shortcuts.</p>
                    <p>First, we stake out the exact footing locations from your plans or your engineer's specs. If you're still working on drawings, we can help you figure out what's needed and walk you through the planning. Once everything is laid out, we dig to the proper depth, which in our part of New Jersey means going below the 36-inch frost line, and we often dig a bit deeper to be safe. Too shallow and the ground can freeze beneath the footing, which lets it heave and puts your whole structure at risk.</p>
                    <p>After we excavate the trench, we check the soil at the bottom. If it's poor, we add crushed stone to improve drainage and build a solid base. Next we build the forms to the correct width and height, then tie the rebar inside by hand, since that steel grid keeps the concrete from cracking under load and it needs to sit in the right spot.</p>
                    <p>During the pour, we use a vibrator to work out air pockets, since trapped air leaves weak spots that show up in older footings. For anything that has to handle our winters, we use air-entrained concrete, a mix with tiny air bubbles that give freezing water somewhere to go, which makes it hold up in our cold, wet climate. It is backed by the <SourceLink num={2}>Portland Cement Association</SourceLink>.</p>
                    <p>After the pour, we screed the top flat so whatever comes next starts level, then let the footing cure before it carries any weight. Active work usually takes a day or two, curing takes longer, and we keep you posted on when it's safe to build. If Totowa requires a footing inspection before the pour, which is typical, we handle the scheduling for you.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 — Full width */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Curing Time and <span className="text-gradient-gold">Inspection Sign-Off</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>This is where a little patience pays off. After the footings are poured, the concrete begins to cure, the process by which it gains strength, and it can't be rushed.</p>
                  <p>Most footings need at least seven days before any real load goes on them, and a full cure takes closer to 28 days. According to the <SourceLink num={3}>American Concrete Institute</SourceLink>, concrete reaches about 70 percent of its design strength by day seven, with the rest developing over the following weeks. It's tempting to start framing or laying block right away, but loading a footing too soon can cause hairline cracks that turn into major issues in the winter.</p>
                  <p>Concrete doesn't just dry; it hardens through a chemical reaction, growing stronger each day. That final stretch of strength matters more than people realize, especially where freeze-thaw puts real stress on anything below grade. We keep the footings damp for the first few days and adapt to the weather, wetting them down or covering them with insulated blankets when temperatures drop. Fresh concrete that freezes before it sets can permanently lose a big share of its strength, so we watch the forecast closely before cold-weather pours.</p>
                  <p>The township signs off before anything is built on the footings, and it's a straightforward process. We call for a footing inspection once the forms are set and the rebar is tied. The inspector checks depth, width, rebar placement, and the soil in the trench. Once those are approved, we pour. We handle the scheduling with the building department, and if an inspector flags anything, we fix it right away. The curing time and inspection are two of the biggest reasons a footing lasts for decades rather than cracking within three years.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 — FAQs */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => {
                  const isDepthQ = faq.question.startsWith("How deep");
                  return (
                    <Card key={index} className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-lg">{faq.question}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {isDepthQ ? (
                          <p className="text-muted-foreground">
                            Footings here need to sit below the 36-inch frost line, which is the minimum across <SourceLink num={1}>northern New Jersey</SourceLink>. Any shallower and you risk frost heave, where the freezing ground pushes the footing out of place. This holds for additions, garages, and deck piers, and we'll dig deeper when the soil underneath is soft or full of old roots.
                          </p>
                        ) : (
                          <p className="text-muted-foreground">{faq.answer}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Section 7 — Ready to Talk Footings */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Talk <span className="text-gradient-gold">Footings?</span>
                </h2>
                <p className="text-lg text-muted-foreground">
                  Based in Totowa and serving <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties, Masonry Pro Construction has poured footings that hold for over 35 years. If you have a project coming up or something that's already moving, call or text us at <a href="tel:+19737252395" className="text-secondary hover:underline">(973) 725-2395</a> for a free estimate. No trip fee, and a straight answer on what your project actually needs.
                </p>
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          <CTASection
            heading="Need"
            highlightedHeading="Concrete Footings?"
            serviceName="Concrete Footings"
          />

          {/* Sources */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Sources</h2>
                <ol className="space-y-4 text-base text-muted-foreground list-decimal pl-6">
                  {sources.map((s) => (
                    <li key={s.num} id={`source-${s.num}`}>
                      {s.text}{" "}
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary hover:underline break-all"
                      >
                        {s.url}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}